import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Download, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { api, track, formatApiError, formatINR } from "@/lib/api";
import { useRazorpaySubscription, PAID_STATUSES } from "@/pages/AppDashboard";
import { useAuth } from "@/context/AuthContext";
import { useSEO } from "@/lib/seo";

const STATUS_STYLE = {
  active: "bg-emerald-100 text-emerald-800", trialing: "bg-emerald-100 text-emerald-800",
  pending_verification: "bg-amber-100 text-amber-800", payment_pending: "bg-amber-100 text-amber-800",
  past_due: "bg-amber-100 text-amber-800", payment_failed: "bg-red-100 text-red-800",
  cancelled: "bg-slate-200 text-slate-700", expired: "bg-slate-200 text-slate-600",
};
const STATUS_LABEL = { active: "Active", trialing: "Trial", pending_verification: "Trial (Verification Pending)",
  payment_pending: "Payment Pending", past_due: "Past Due", payment_failed: "Payment Failed",
  cancelled: "Cancelled", expired: "Expired" };

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function Subscription() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [success, setSuccess] = useState(false);

  useSEO({ title: "Subscription & Billing — Mashara Finance", description: "Manage your Mashara Finance subscription.", path: "/app/subscription", noindex: true });

  const load = useCallback(() => {
    api.get("/billing/subscription").then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user === null) navigate("/login");
    else if (user) load();
  }, [user, navigate, load]);

  const { startCheckout, paying } = useRazorpaySubscription((label) => {
    if (label) { setSuccess(true); track("subscription_activated", { label }); }
    load();
  });

  const changePlan = async (planId) => {
    try {
      const { data: result } = await api.post("/billing/change-plan", { plan_id: planId, billing_cycle: billingCycle });
      if (result.change_type === "downgrade") {
        toast.success(result.detail);
        load();
      } else {
        // upgrade: open checkout for the new subscription
        const ok = await import("@/lib/api").then((m) => m.loadRazorpayScript());
        if (!ok) throw new Error("gateway load failed");
        const rzp = new window.Razorpay({
          key: result.key_id, subscription_id: result.subscription_id,
          name: "Mashara Finance", description: result.label, theme: { color: "#047857" },
          modal: { ondismiss: () => toast.info("Checkout closed. No payment was made.") },
          handler: async (resp) => {
            try {
              await api.post("/billing/verify-subscription", {
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_subscription_id: resp.razorpay_subscription_id,
                razorpay_signature: resp.razorpay_signature,
              });
              setSuccess(true);
              load();
            } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
          },
        });
        rzp.open();
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const cancel = async () => {
    try {
      const { data: result } = await api.post("/billing/cancel");
      track("cancel");
      toast.success(`Subscription cancelled. Access until ${fmtDate(result.access_until)}.`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (!data) return <div className="py-32 text-center text-slate-500">Loading subscription…</div>;

  const sub = data.subscription;
  const plan = data.plan;
  const isPaid = PAID_STATUSES.includes(sub.status);
  const price = plan ? (sub.billing_cycle === "annual" ? plan.annual_price : plan.monthly_price) : null;

  return (
    <div data-testid="subscription-page" className="py-10 sm:py-16 bg-slate-50 min-h-screen">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <button onClick={() => navigate("/app")} data-testid="back-to-dashboard"
          className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-emerald-700 mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </button>

        {success && (
          <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-5 mb-6" data-testid="sub-success-banner">
            <p className="font-semibold text-emerald-900">Payment successful — your subscription is active.</p>
          </div>
        )}
        {sub.status === "payment_failed" && (
          <div className="rounded-2xl bg-red-50 border border-red-200 p-5 mb-6 flex items-start gap-3" data-testid="sub-payment-failed">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-red-900">Your last payment failed</p>
              <p className="text-sm text-red-700 mt-0.5">Your data is safe. Retry payment below to keep your access. Access continues until {fmtDate(sub.end_date)}.</p>
            </div>
          </div>
        )}
        {data.pending_plan && (
          <div className="rounded-2xl bg-amber-50 border border-amber-200 p-5 mb-6" data-testid="pending-downgrade-note">
            <p className="font-semibold text-amber-900">Downgrade scheduled</p>
            <p className="text-sm text-amber-800 mt-0.5">
              Your plan changes to <strong>{data.pending_plan.name}</strong> on {fmtDate(sub.end_date)}. No existing users or centers will be removed; new limits ({data.pending_plan.users}, {data.pending_plan.centers}) will apply to new additions after that date.
            </p>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 mb-6" data-testid="current-plan-card">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700 mb-1">Current Plan</p>
              <h1 className="font-display text-3xl font-bold text-slate-900" data-testid="current-plan-name">{plan?.name || "Trial"}</h1>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span data-testid="sub-status-badge" className={`text-xs font-bold rounded-full px-3 py-1 ${STATUS_STYLE[sub.status] || "bg-slate-100"}`}>
                  {STATUS_LABEL[sub.status] || sub.status}
                </span>
                {sub.billing_cycle && isPaid && <span className="text-xs text-slate-500 capitalize">{sub.billing_cycle} billing</span>}
                {price !== null && isPaid && <span className="text-xs font-semibold text-slate-700">{formatINR(price)}/{sub.billing_cycle === "annual" ? "yr" : "mo"}</span>}
              </div>
            </div>
            {isPaid && sub.status !== "cancelled" && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" data-testid="cancel-subscription-button" className="rounded-xl border-red-200 text-red-600 hover:bg-red-50">Cancel Subscription</Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="cancel-confirm-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Cancel your subscription?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Your access continues until {fmtDate(sub.end_date)}. No further renewals will be charged. Your data stays safe.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="cancel-keep-button">Keep Subscription</AlertDialogCancel>
                    <AlertDialogAction data-testid="cancel-confirm-button" onClick={cancel} className="bg-red-600 hover:bg-red-700">Yes, Cancel</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            {sub.status === "cancelled" && (
              <p className="text-sm text-slate-600" data-testid="cancelled-info">Cancelled on {fmtDate(sub.cancelled_at)} · Access until {fmtDate(sub.end_date)}</p>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="rounded-xl bg-slate-50 p-4">
              <p className="text-xs text-slate-500">Users</p>
              <p className="font-bold text-slate-900" data-testid="sub-usage-users">{data.usage.users} / {data.limits.users ?? "Unlimited"}</p>
            </div>
            <div className="rounded-xl bg-slate-50 p-4">
              <p className="text-xs text-slate-500">Centers</p>
              <p className="font-bold text-slate-900" data-testid="sub-usage-centers">{data.usage.centers} / {data.limits.centers ?? "Unlimited"}</p>
            </div>
            <div className="rounded-xl bg-slate-50 p-4">
              <p className="text-xs text-slate-500">{sub.status === "trialing" ? "Trial Ends" : "Next Billing"}</p>
              <p className="font-bold text-slate-900" data-testid="sub-next-billing">
                {sub.status === "trialing" ? `${data.trial_days_left ?? 0} days left` : fmtDate(sub.next_billing_date)}
              </p>
            </div>
            <div className="rounded-xl bg-slate-50 p-4">
              <p className="text-xs text-slate-500">Started</p>
              <p className="font-bold text-slate-900" data-testid="sub-start-date">{fmtDate(sub.start_date || sub.created_at)}</p>
            </div>
          </div>
        </div>

        {/* Plan picker */}
        <div className="bg-slate-900 text-white rounded-2xl p-6 sm:p-8 mb-6" data-testid="plan-picker">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <h2 className="font-display font-bold text-xl">{isPaid ? "Upgrade / Change Plan" : "Subscribe Now"}</h2>
            <div className="inline-flex bg-slate-800 rounded-full p-1" data-testid="sub-billing-toggle">
              <button data-testid="sub-monthly" onClick={() => setBillingCycle("monthly")}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold ${billingCycle === "monthly" ? "bg-emerald-600 text-white" : "text-slate-400"}`}>Monthly</button>
              <button data-testid="sub-annual" onClick={() => setBillingCycle("annual")}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold ${billingCycle === "annual" ? "bg-emerald-600 text-white" : "text-slate-400"}`}>Annual</button>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {(data.plans || []).map((p) => {
              const isCurrent = isPaid && plan?.id === p.id && sub.billing_cycle === billingCycle;
              return (
                <div key={p.id} data-testid={`sub-plan-${p.id}`} className={`rounded-xl border p-5 ${isCurrent ? "border-emerald-500" : "border-slate-700"}`}>
                  <div className="flex items-center justify-between">
                    <p className="font-semibold">{p.name}</p>
                    {isCurrent && <span className="text-[10px] font-bold bg-emerald-600 rounded-full px-2 py-0.5" data-testid={`sub-current-badge-${p.id}`}>CURRENT</span>}
                  </div>
                  <p className="font-display text-2xl font-bold mt-2">{formatINR(billingCycle === "annual" ? p.annual_price : p.monthly_price)}<span className="text-xs text-slate-400 font-normal">/{billingCycle === "annual" ? "yr" : "mo"}</span></p>
                  <p className="text-xs text-slate-400 mt-1.5">{p.users} · {p.centers}</p>
                  <Button size="sm" data-testid={`sub-buy-${p.id}`} disabled={paying || isCurrent || !data.payments_configured}
                    onClick={() => isPaid ? changePlan(p.id) : startCheckout(p.id, billingCycle)}
                    className="mt-4 w-full rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs disabled:opacity-50">
                    {isCurrent ? "Current Plan" : isPaid ? (p.monthly_price > (plan?.monthly_price || 0) ? "Upgrade Plan" : "Downgrade (at cycle end)") : "Subscribe Now"}
                    <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Payment history */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8" data-testid="payment-history">
          <h2 className="font-display font-bold text-xl text-slate-900 mb-4">Payment History</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                  <th className="pb-3 pr-4">Date</th><th className="pb-3 pr-4">Description</th><th className="pb-3 pr-4">Amount</th>
                  <th className="pb-3 pr-4">Payment ID</th><th className="pb-3 pr-4">Status</th><th className="pb-3">Receipt</th>
                </tr>
              </thead>
              <tbody>
                {(data.payments || []).map((p) => (
                  <tr key={p.id} className="border-b border-slate-100" data-testid={`payment-row-${p.id}`}>
                    <td className="py-3 pr-4 text-slate-600">{fmtDate(p.paid_at || p.created_at)}</td>
                    <td className="py-3 pr-4 text-slate-800">{p.label}{p.billing_cycle ? ` · ${p.billing_cycle}` : ""}</td>
                    <td className="py-3 pr-4 font-semibold">{p.amount ? formatINR(p.amount / 100) : "—"}</td>
                    <td className="py-3 pr-4 text-xs text-slate-500 font-mono">{p.razorpay_payment_id || p.payment_id || "—"}</td>
                    <td className="py-3 pr-4">
                      <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${p.status === "paid" ? "bg-emerald-100 text-emerald-800" : p.status === "failed" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>{p.status}</span>
                    </td>
                    <td className="py-3">
                      {p.receipt_url ? (
                        <a href={p.receipt_url} target="_blank" rel="noopener noreferrer" data-testid={`receipt-${p.id}`}
                          className="text-emerald-700 hover:text-emerald-800"><Download className="w-4 h-4" /></a>
                      ) : <span className="text-xs text-slate-400">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(data.payments || []).length === 0 && (
              <p className="text-sm text-slate-500 py-8 text-center">No payments yet. Your receipts will appear here after your first payment.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
